import { onMounted, onUnmounted } from 'vue'

// Generic active-duration tracker, keyed by an arbitrary session key
// (e.g. `${moduleCode}:${studyId}`) — no Time-Study-specific knowledge, so any
// future module's wizard shell can reuse it with its own key. See
// docs/Behavioral_Analysis_Mixpanel.md §4d.

const STORAGE_PREFIX = 'active_duration:'
const IDLE_THRESHOLD_MS = 2 * 60 * 1000 // matches RebaRulaAssessment.vue's IDLE_THRESHOLD_MS
const AUTOSAVE_INTERVAL_MS = 20 * 1000
const ACTIVITY_THROTTLE_MS = 1000
const STALE_SWEEP_MS = 30 * 24 * 60 * 60 * 1000

function storageKey(sessionKey) {
  return `${STORAGE_PREFIX}${sessionKey}`
}

function readStoredMs(sessionKey) {
  try {
    const raw = localStorage.getItem(storageKey(sessionKey))
    if (!raw) return 0
    const parsed = JSON.parse(raw)
    return typeof parsed.activeMs === 'number' ? parsed.activeMs : 0
  } catch {
    return 0
  }
}

function writeStoredMs(sessionKey, activeMs) {
  try {
    localStorage.setItem(
      storageKey(sessionKey),
      JSON.stringify({ activeMs, lastFlushedAt: Date.now() })
    )
  } catch {
    // localStorage unavailable (private mode, quota, etc.) — active duration
    // just won't survive a reload; not fatal.
  }
}

// Removes any active_duration:* entries untouched for 30+ days — abandoned
// studies that never fired a completion event, so storage doesn't grow
// unbounded. Safe to call once at app init.
export function sweepStaleActiveSessions() {
  try {
    const now = Date.now()
    Object.keys(localStorage)
      .filter((key) => key.startsWith(STORAGE_PREFIX))
      .forEach((key) => {
        try {
          const { lastFlushedAt } = JSON.parse(localStorage.getItem(key))
          if (!lastFlushedAt || now - lastFlushedAt > STALE_SWEEP_MS) {
            localStorage.removeItem(key)
          }
        } catch {
          localStorage.removeItem(key)
        }
      })
  } catch {
    // localStorage unavailable — nothing to sweep
  }
}

export function useActiveSession() {
  let currentKey = null
  let accumulatedMs = 0
  let burstStart = null
  let lastInteractionMs = null
  let lastActivityTickMs = 0
  let autosaveTimer = null

  function persist() {
    if (!currentKey) return
    writeStoredMs(currentKey, accumulatedMs)
  }

  // Folds the in-flight activity burst into accumulatedMs and pauses it, without
  // resetting the accumulated total — the next recordActivity() resumes counting
  // rather than starting over. Mirrors RebaRulaAssessment.vue's
  // _flushActiveSeconds accumulation logic, but repeatable rather than one-shot.
  function flushBurst() {
    if (burstStart !== null && lastInteractionMs !== null) {
      accumulatedMs += lastInteractionMs - burstStart
    }
    burstStart = null
    lastInteractionMs = null
  }

  function recordActivity() {
    if (!currentKey) return
    const now = Date.now()
    if (now - lastActivityTickMs < ACTIVITY_THROTTLE_MS) return
    lastActivityTickMs = now

    if (burstStart === null) {
      burstStart = now
    } else if (lastInteractionMs !== null && now - lastInteractionMs > IDLE_THRESHOLD_MS) {
      accumulatedMs += lastInteractionMs - burstStart
      persist()
      burstStart = now
    }
    lastInteractionMs = now
  }

  // Persists the current session's accumulated time and pauses the in-flight
  // burst. Safe to call repeatedly (idle timeout, tab hidden, periodic autosave) —
  // activity after a flush resumes counting rather than restarting from zero.
  function flushSession() {
    flushBurst()
    persist()
  }

  // Flushes the outgoing session (if any) and starts/resumes the incoming one,
  // rehydrating any previously accumulated time for that key from localStorage.
  // This is what makes reload / multi-day / multi-study-switch resume correctly —
  // state lives in localStorage keyed by session, not in component memory.
  function switchSession(sessionKey) {
    if (sessionKey === currentKey) return
    if (currentKey) flushSession()

    currentKey = sessionKey
    accumulatedMs = sessionKey ? readStoredMs(sessionKey) : 0
    burstStart = null
    lastInteractionMs = null
  }

  // Whole-seconds accumulated for a session. Flushes first when it's the
  // currently active session, so a read right before sending a completion event
  // reflects activity up to "now," not just the last periodic autosave.
  function getActiveSeconds(sessionKey) {
    if (sessionKey === currentKey) flushSession()
    return Math.round(readStoredMs(sessionKey) / 1000)
  }

  // Call after sending a completion event — removes the persisted entry for a
  // session so storage doesn't keep a finished study around indefinitely.
  function clearSession(sessionKey) {
    try {
      localStorage.removeItem(storageKey(sessionKey))
    } catch {
      // localStorage unavailable — nothing to clear
    }
    if (sessionKey === currentKey) {
      currentKey = null
      accumulatedMs = 0
      burstStart = null
      lastInteractionMs = null
    }
  }

  function handleVisibilityChange() {
    if (document.visibilityState === 'hidden') flushSession()
  }

  onMounted(() => {
    document.addEventListener('click', recordActivity)
    document.addEventListener('keydown', recordActivity)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    autosaveTimer = setInterval(flushSession, AUTOSAVE_INTERVAL_MS)
  })

  onUnmounted(() => {
    document.removeEventListener('click', recordActivity)
    document.removeEventListener('keydown', recordActivity)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    if (autosaveTimer) clearInterval(autosaveTimer)
    flushSession()
  })

  return { switchSession, flushSession, getActiveSeconds, clearSession }
}
